import { useCallback, useEffect, useState } from "react";
import { getTransactionHistory } from "../../services";
import { TransactionVersion } from "../../types/sale.type";

/**
 * Load the version list for one bill and track which version is being viewed.
 *
 * Mirrors the sale report's arrangement in `pages/ReportDetailsPage.tsx`, where the
 * history is fetched independently of the detail and the detail read waits on it. The
 * important difference: the report page never issues a detail request without a
 * concrete `?version=`, because a null version there merges every version's rows into
 * one list. A bill has no such hazard — its items are joined on the surrogate key, so
 * an unversioned read is simply the current version. So `selected` starting undefined
 * is safe here, and the detail page fetches immediately rather than waiting a round
 * trip. That keeps the screen exactly as fast as it was before versions existed.
 *
 * A failed history lookup is swallowed on purpose. The picker is an enhancement on top
 * of the bill; if it cannot load, the bill itself must still render. Errors from the
 * request are already surfaced by the axios interceptor, so there is nothing to add.
 */
export function useTransactionVersions(uuid?: string) {
  const [versions, setVersions] = useState<TransactionVersion[]>([]);
  const [selected, setSelected] = useState<TransactionVersion>();

  const load = useCallback(async (transactionUuid: string) => {
    try {
      const {
        data: { data },
      } = await getTransactionHistory(transactionUuid);
      const histories = data?.histories ?? [];
      setVersions(histories);
      // Open on the live version, falling back to the newest entry when nothing is
      // marked current (every range closed — a cancelled-and-replaced bill).
      setSelected(
        histories.find((entry) => entry.is_current) ??
          histories[histories.length - 1],
      );
    } catch {
      setVersions([]);
      setSelected(undefined);
    }
  }, []);

  useEffect(() => {
    if (uuid) load(uuid);
  }, [uuid, load]);

  // The current version is read without a `?version=` param so the request stays
  // byte-identical to the pre-versioning one.
  const versionParam =
    selected && !selected.is_current ? selected.version : undefined;

  return { versions, selected, setSelected, versionParam };
}
